import { computed } from 'vue'
import { COLLECTIONS, type AlgoliaLikeRequestOptions } from '~/lib/search/typesense'

/**
 * Replacements for @nuxtjs/algolia's useAlgoliaSearch / useAsyncAlgoliaSearch on Typesense.
 * Same signatures and Algolia-shaped results as before the switch.
 */

interface SearchArgs {
  query: string
  requestOptions?: AlgoliaLikeRequestOptions
}

export function useSearch(indexName?: string) {
  const index = indexName || COLLECTIONS.products
  const result = useState<any>(`${index}-search-result`, () => null)

  const search = async ({ query, requestOptions }: SearchArgs) => {
    const searchResult = await useTypesenseSearch().search(index, query, requestOptions)
    result.value = searchResult
    return searchResult
  }

  return {
    result: computed(() => result.value),
    search
  }
}

export async function useAsyncSearch({ query, requestOptions, indexName, key }: SearchArgs & { indexName?: string, key?: string }) {
  const index = indexName || COLLECTIONS.products
  const typesense = useTypesenseSearch()

  return useAsyncData(`${index}-async-search-result-${key ?? ''}`, () => typesense.search(index, query, requestOptions))
}

/** Several searches in one multi_search request (quick search panel). Call in setup; the returned function runs the searches. */
export function useMultiSearch(indexNames: (string | undefined)[]) {
  const indexes = indexNames.map(name => name || COLLECTIONS.products)
  const typesense = useTypesenseSearch()
  return (requests: SearchArgs[]) => typesense.multiSearch(requests.map((request, i) => ({
    indexName: indexes[i],
    query: request.query,
    options: request.requestOptions
  })))
}
